/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Centralized encryption utilities using environment variables (Option 1)
 *
 * This provides a unified encryption interface for all sensitive data.
 * Keys are stored in Supabase environment variables for security.
 *
 * Future upgrade path: Replace getCryptoKeys() for user-specific keys (Option 2)
 */

export interface CryptoKeys {
  key: string;
  iv: string;
  method: string;
}

/**
 * Get encryption keys from environment variables
 *
 * For Option 1: Returns same keys for all users
 * For Option 2 upgrade: Will accept userId parameter for user-specific keys
 */
export async function getCryptoKeys(): Promise<CryptoKeys> {
  // Check if running in server environment (Edge Functions) or client
  const isServer = typeof globalThis !== "undefined" && "Deno" in globalThis;

  if (isServer) {
    // Server-side: Get from Deno environment
    const Deno = (globalThis as any).Deno;
    const masterKey = Deno.env.get("ENCRYPTION_MASTER_KEY");
    const ivSecret = Deno.env.get("ENCRYPTION_IV_SECRET");

    if (!masterKey || !ivSecret) {
      throw new Error("Encryption keys not configured in environment variables");
    }

    return {
      key: masterKey,
      iv: ivSecret,
      method: "AES-256-CBC",
    };
  } else {
    // Client-side: Use placeholder keys for now
    // TODO: Implement proper client-side key management
    console.warn("Using placeholder encryption keys on client-side");
    return {
      key: "placeholder-master-key-for-client-side-encryption-dev",
      iv: "placeholder-iv-secret-for-client-side-dev",
      method: "AES-256-CBC",
    };
  }
}

/**
 * Encrypt sensitive data using AES-256-CBC
 */
export async function encryptData(plaintext: string, cryptoKeys?: CryptoKeys): Promise<string> {
  const keys = cryptoKeys || (await getCryptoKeys());

  // Generate key and IV using the same process as existing hooks
  const keyBytes = await generateKey(keys.key);
  const ivBytes = await generateIV(keys.iv);

  // Import the key for AES-CBC encryption
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes as BufferSource,
    { name: "AES-CBC" },
    false,
    ["encrypt"],
  );

  // Convert plaintext to bytes
  const plaintextBytes = new TextEncoder().encode(plaintext);

  // Encrypt using AES-CBC
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-CBC",
      iv: ivBytes as BufferSource,
    },
    cryptoKey,
    plaintextBytes,
  );

  // Convert encrypted bytes to hex string (matching backend format)
  const encryptedArray = new Uint8Array(encryptedBuffer);
  const hexString = Array.from(encryptedArray)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Convert hex string to base64 (matching backend format)
  const base64Result = btoa(hexString);

  return base64Result;
}

/**
 * Decrypt sensitive data using AES-256-CBC
 */
export async function decryptData(encryptedData: string, cryptoKeys?: CryptoKeys): Promise<string> {
  const keys = cryptoKeys || (await getCryptoKeys());

  // Generate key and IV using the same process as existing hooks
  const keyBytes = await generateKey(keys.key);
  const ivBytes = await generateIV(keys.iv);

  // Import the key for AES-CBC decryption
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes as BufferSource,
    { name: "AES-CBC" },
    false,
    ["decrypt"],
  );

  // Convert base64 to hex to bytes
  const hexString = atob(encryptedData);
  const encryptedBytes = new Uint8Array(
    hexString.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)),
  );

  // Decrypt using AES-CBC
  const decryptedBuffer = await crypto.subtle.decrypt(
    {
      name: "AES-CBC",
      iv: ivBytes as BufferSource,
    },
    cryptoKey,
    encryptedBytes,
  );

  // Convert decrypted bytes back to string
  const decryptedText = new TextDecoder().decode(decryptedBuffer);

  return decryptedText;
}

/**
 * Generate crypto key from secret - matches existing decryption utility
 */
async function generateKey(secretKey: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const data = encoder.encode(secretKey);
  const hashBuffer = await crypto.subtle.digest("SHA-512", data);
  const hashArray = new Uint8Array(hashBuffer);
  const hashHex = Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Take first 32 hex chars like backend, then convert to bytes AS STRING
  const backendKeyHex = hashHex.substring(0, 32);
  const keyBytes = new TextEncoder().encode(backendKeyHex);

  return keyBytes;
}

/**
 * Generate IV from secret - matches existing decryption utility
 */
async function generateIV(secretIv: string): Promise<Uint8Array> {
  const encoder = new TextEncoder();
  const data = encoder.encode(secretIv);
  const hashBuffer = await crypto.subtle.digest("SHA-512", data);
  const hashArray = new Uint8Array(hashBuffer);
  const hashHex = Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  // Take first 16 hex chars like backend, then convert to bytes AS STRING
  const backendIvHex = hashHex.substring(0, 16);
  const ivBytes = new TextEncoder().encode(backendIvHex);

  return ivBytes;
}

/**
 * Encryption configuration constants
 */
export const ENCRYPTION_CONFIG = {
  algorithm: "AES-256-CBC",
  keyLength: 256, // bits
  ivLength: 128, // bits
  version: "1.0", // For future migration tracking
} as const;
